import { Autocomplete, Box, Checkbox, FormControlLabel, Paper, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import DatePicker from "../../../components/common/DatePicker";
import Subheading from "../../../components/common/Subheading";
import Subtitle from "../../../components/common/Subtitle";
import { PLATFORM_NAMES } from "../constants/platformNames";

/**
 * TaskInputs component for the Auto4Client feature.
 *
 * This component renders a form for data preparation inputs, including company selection and date range selection.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array<string>} props.companyNames - Array of company names available for selection.
 * @param {Object} props.taskInputs - Object containing the current task inputs.
 * @param {Function} props.onInputsChange - Callback function to notify parent about state changes.
 *
 * @returns {JSX.Element} - The JSX element for the task inputs form.
 */
function TaskInputs({ companyNames, taskInputs, onInputsChange }) {
  const handleDateChange = (field) => (val) => {
    onInputsChange((prevState) => ({
      ...prevState,
      [field]: val,
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    onInputsChange((prevState) => ({
      ...prevState,
      [field]: event.target.checked,
    }));
  };

  const handleCompanyChange = (event, value) => {
    onInputsChange({ ...taskInputs, selectedCompany: value });
  };

  const handleDataNameChange = (event) => {
    onInputsChange({ ...taskInputs, dataName: event.target.value });
  };

  const handlePlatformChange = (event, value) => {
    const nextPlatforms = taskInputs.platforms.includes(value)
      ? taskInputs.platforms.filter((p) => p !== value)
      : [...taskInputs.platforms, value];
    onInputsChange({ ...taskInputs, platforms: nextPlatforms });
  };

  return (
    <Grid container direction="column" wrap="nowrap" spacing={4} p={{ xs: 2, md: 3 }} mx={{ xs: 0, md: "10%" }}>
      {/* Company Selection Section */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Subheading text="Company Selection" sx={{ mb: 3 }} />
        <Autocomplete
          options={companyNames}
          renderInput={(params) => <TextField {...params} label="Select Company" />}
          onChange={handleCompanyChange}
          fullWidth
          size="medium"
        />
      </Paper>

      {/* Date Range Section */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Subheading text="Date Range" sx={{ mb: 3 }} />
        <Grid container spacing={3} justifyContent="center">
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box
              flexDirection="column"
              borderRadius="15px"
              p={1}
              sx={{ backgroundColor: "#121212", display: "flex", justifyContent: "center", textAlign: "center" }}
            >
              <Subtitle text="Start Date" sx={{ fontSize: "1.15rem", py: 1 }} />
              <DatePicker currentVal={taskInputs.startDate} handleVal={handleDateChange("startDate")} />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ textAlign: "center" }}>
            <Box
              flexDirection="column"
              borderRadius="15px"
              p={1}
              sx={{ backgroundColor: "#121212", display: "flex", justifyContent: "center" }}
            >
              <Subtitle text="End Date" sx={{ fontSize: "1.15rem", py: 1 }} />
              <DatePicker currentVal={taskInputs.endDate} handleVal={handleDateChange("endDate")} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Task Execution Options Section */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Subheading text="Task Execution Options" sx={{ mb: 3 }} />
        <Box display="flex" flexDirection="column">
          <FormControlLabel
            control={
              <Checkbox
                checked={taskInputs.overwriteExistingDBRows ? true : false}
                onChange={handleCheckboxChange("overwriteExistingDBRows")}
              />
            }
            label="Overwrite Existing DB Rows"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={taskInputs.includePreviouslyClassified ? true : false}
                onChange={handleCheckboxChange("includePreviouslyClassified")}
              />
            }
            label="Include Previously Classified"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={taskInputs.competitiveAnalysis ? true : false}
                onChange={handleCheckboxChange("competitiveAnalysis")}
              />
            }
            label="Competitive Analysis"
          />
        </Box>
      </Paper>

      {/* Platforms Section */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Subheading text="Platforms" sx={{ mb: 3 }} />
        <Box display="flex" flexDirection="column">
          {PLATFORM_NAMES.map((platform) => (
            <FormControlLabel
              key={platform}
              control={
                <Checkbox
                  checked={taskInputs.platforms.includes(platform) ? true : false}
                  onChange={(event) => handlePlatformChange(event, platform)}
                />
              }
              label={platform}
            />
          ))}
        </Box>
      </Paper>

      {/* Data Name Section */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Subheading text="Data Name" sx={{ mb: 3 }} />
        <FormControlLabel
          control={<Checkbox checked={taskInputs.dataName ? true : false} onChange={handleCheckboxChange("dataName")} />}
          label="Data Name"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Data Name"
          value={taskInputs.dataName}
          onChange={handleDataNameChange}
          fullWidth
          size="medium"
          disabled={!taskInputs.dataName}
        />
      </Paper>
    </Grid>
  );
}

export default TaskInputs;
